/** Eksportene under «agency-tour» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/agency-tour/`. */
import type { ExportMeta } from '../types.js';

/* Id-ene er `agency.*` (domenet; skjemaet tillater ikke bindestrek foran punktumet), tekstene `export.agencyTour.*`. */
const T = 'export.agencyTour.';

/**
 * KONSERTER (byrået): de solgte og de egne, slik Bookinger på artistsiden slår dem sammen —
 * giggene hos arrangørene, forespørslene, bookingene byrået førte selv og byråets egne konserter.
 * `object` er én artist, `selection` merkede artister, `filter` artistsidens filter (artist, solgt
 * eller egen, kommende/tidligere, søket) eller hele stallen når filteret ikke nevner en artist.
 * Perioden (`from`/`to`) står i filteret eller i `params`.
 */
const CONCERTS: ExportMeta = {
  id: 'agency.concerts',
  domain: 'agency',
  kind: 'table',
  titleKey: T + 'concerts.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'artist-bookings',
  columns: [
    { key: 'date', labelKey: T + 'concerts.columns.date', type: 'date' },
    { key: 'endsOn', labelKey: T + 'concerts.columns.endsOn', type: 'date', defaultHidden: true },
    { key: 'artist', labelKey: T + 'concerts.columns.artist', type: 'text' },
    { key: 'who', labelKey: T + 'concerts.columns.who', type: 'text' },
    { key: 'where', labelKey: T + 'concerts.columns.where', type: 'text' },
    { key: 'type', labelKey: T + 'concerts.columns.type', type: 'status' },
    { key: 'format', labelKey: T + 'concerts.columns.format', type: 'text' },
    { key: 'party', labelKey: T + 'concerts.columns.party', type: 'integer' },
    { key: 'state', labelKey: T + 'concerts.columns.state', type: 'status' },
    /* Beløpet er `null` fra tjenestene uten `canSeeBookingAmounts`; da tas kolonnen ut. */
    { key: 'fee', labelKey: T + 'concerts.columns.fee', type: 'money', rule: 'booking_amounts' },
    {
      key: 'breakEven',
      labelKey: T + 'concerts.columns.breakEven',
      type: 'integer',
      defaultHidden: true,
    },
    { key: 'advance', labelKey: T + 'concerts.columns.advance', type: 'status' },
    { key: 'time', labelKey: T + 'concerts.columns.time', type: 'time', defaultHidden: true },
    { key: 'stage', labelKey: T + 'concerts.columns.stage', type: 'text', defaultHidden: true },
    {
      key: 'respondsBy',
      labelKey: T + 'concerts.columns.respondsBy',
      type: 'date',
      defaultHidden: true,
    },
    {
      key: 'assignee',
      labelKey: T + 'concerts.columns.assignee',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'terms',
      labelKey: T + 'concerts.columns.terms',
      type: 'multiline',
      defaultHidden: true,
    },
    {
      key: 'message',
      labelKey: T + 'concerts.columns.message',
      type: 'multiline',
      defaultHidden: true,
    },
    {
      key: 'createdAt',
      labelKey: T + 'concerts.columns.createdAt',
      type: 'date',
      defaultHidden: true,
    },
  ],
  landscape: true,
};

/**
 * TURNÉPLAN: ett dokument per artist med datoene i perioden (de solgte og de egne), en side per
 * dato med sted, tider og kontakter — fra den delte advancen når den finnes — og artistens
 * utilgjengelige dager og egne kontakter.
 */
const TOUR_PLAN: ExportMeta = {
  id: 'agency.tour-plan',
  domain: 'agency',
  kind: 'document',
  titleKey: T + 'tourPlan.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  sections: [
    { key: 'dates', labelKey: T + 'tourPlan.sections.dates' },
    { key: 'days', labelKey: T + 'tourPlan.sections.days' },
    { key: 'calendar', labelKey: T + 'tourPlan.sections.calendar' },
    { key: 'contacts', labelKey: T + 'tourPlan.sections.contacts' },
  ],
};

/**
 * REISEFØLGET: ett dokument per artist, slik Stamdata › Reisefølge viser det — navn, rolle,
 * instrument, telefon, e-post, hvor de reiser fra og formatene — og kosthold og allergier med navn.
 * `params.formatId` gir bare dem som er med i det formatet (filteret på fanen).
 */
const PARTY: ExportMeta = {
  id: 'agency.touring-party',
  domain: 'agency',
  kind: 'document',
  titleKey: T + 'party.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  sections: [
    { key: 'party', labelKey: T + 'party.sections.party' },
    { key: 'diet', labelKey: T + 'party.sections.diet' },
  ],
  landscape: true,
};

/** Delene i festivalens advance, med de samme nøklene og tekstene: oppsettet er det samme. */
const ADVANCE_SECTIONS = [
  'times',
  'party',
  'travel',
  'stay',
  'local',
  'technical',
  'hospitality',
  'promo',
  'diet',
  'guests',
  'contacts',
  'documents',
] as const;

/**
 * ADVANCEN FRA BYRÅETS SIDE: det byrået ser av den delte advancen, i festivalens oppsett
 * (`festival.advance`) og aldri med festivalens økonomi (249.18). `object` er advancen
 * (`/artists/:id/advance/:advanceId`), `parent` alle de delte advancene til én artist, `selection`
 * merkede artister.
 */
const ADVANCE: ExportMeta = {
  id: 'agency.advance',
  domain: 'agency',
  kind: 'document',
  titleKey: T + 'advance.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'parent', 'selection'],
  requires: ['performance:read'],
  guest: false,
  sections: [
    ...ADVANCE_SECTIONS.map((key) => ({
      key,
      labelKey: 'export.festival.advance.sections.' + key,
    })),
    {
      key: 'contract',
      labelKey: 'export.festival.advance.sections.contract',
      requires: ['contract:read'],
    },
  ],
};

/**
 * DAGSARKET FRA BYRÅETS SIDE: festivalens dagsark (`festival.day-sheet`) for den delte posten —
 * tidsplanen per dag, kontaktene og hotellet. Det turnélederen har i lomma. Samme utvalg som advancen.
 */
const DAY_SHEET: ExportMeta = {
  id: 'agency.day-sheet',
  domain: 'agency',
  kind: 'document',
  titleKey: T + 'daySheet.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'parent', 'selection'],
  requires: ['performance:read'],
  guest: false,
  sections: [
    { key: 'schedule', labelKey: 'export.festivalProgram.daySheet.sections.schedule' },
    { key: 'contacts', labelKey: 'export.festivalProgram.daySheet.sections.contacts' },
    { key: 'stay', labelKey: 'export.festivalProgram.daySheet.sections.stay' },
  ],
};

export const AGENCY_TOUR_EXPORTS: readonly ExportMeta[] = [
  CONCERTS,
  TOUR_PLAN,
  PARTY,
  ADVANCE,
  DAY_SHEET,
];
